/**
 * compoundKoreanName — 화합물 한국어 통용명 매핑 utility (iter2.2 C1).
 *
 * 시연용 display helper — backend 변경 없이 frontend에서 영문 화합물명 ↔ 한국어
 * 통용명 매핑. 인하대 공식 매핑 또는 142종 taxonomy 도착 시 이 utility 내부
 * MAPPING 객체만 교체하면 됨. iter3에서 backend 이전 가능.
 *
 * 입력: predicted_substance (영문)
 * 출력: { ko, en, hasMapping }
 *   - hasMapping=true: ko에 한국어 통용명
 *   - hasMapping=false: ko에 영문 그대로 (억지 번역 X)
 *
 * 매핑 우선순위: mockup features 12개 + MSW handler features + BTEX/n-알칸/케톤·알데히드/할로겐 핵심.
 */

export interface KoreanNameResult {
  /** 한국어 통용명 (또는 fallback 영문) */
  ko: string;
  /** 원문 영문 */
  en: string;
  /** 매핑 존재 여부 */
  hasMapping: boolean;
}

// 영문 → 한국어 매핑. 가나다 순.
const MAPPING: Record<string, string> = {
  // ── 방향족 (BTEX + 확장) ───────────────────────────────────────
  Benzene: "벤젠",
  Toluene: "톨루엔",
  Ethylbenzene: "에틸벤젠",
  "m-Xylene": "m-자일렌",
  "p-Xylene": "p-자일렌",
  "n-Xylene": "n-자일렌",
  "o-Xylene": "o-자일렌",
  Naphthalene: "나프탈렌",
  Styrene: "스티렌",

  // ── 케톤·알데히드 ──────────────────────────────────────────────
  Benzaldehyde: "벤즈알데히드",
  Acetone: "아세톤",
  "Methyl ethyl ketone": "메틸에틸케톤 (MEK)",
  Acrolein: "아크롤레인",
  Furfural: "푸르푸랄",

  // ── 할로겐화 ──────────────────────────────────────────────────
  "Benzyl chloride": "벤질 클로라이드",
  "Methylene chloride": "메틸렌 클로라이드",
  Chloroform: "클로로포름",

  // ── n-알칸 (n-카르본) ─────────────────────────────────────────
  "n-Octane": "n-옥탄",
  "n-Nonane": "n-노난",
  "n-Decane": "n-데칸",
  "n-Undecane": "n-운데칸",
  "n-Dodecane": "n-도데칸",
  "n-Tridecane": "n-트리데칸",
  "n-Tetradecane": "n-테트라데칸",
  "n-Pentadecane": "n-펜타데칸",
  "n-Hexadecane": "n-헥사데칸",

  // ── 시클로/헤테로 환 ─────────────────────────────────────────
  Cyclohexane: "시클로헥산",
  Furan: "푸란",
  Naphthenes: "나프텐류",

  // ── mockup 그룹/혼합 (featuresSample 12종 매칭) ──────────────────
  "Gas Background (CO, N2, O2, CO2)": "가스 백그라운드",
  "Benzene Fragment": "벤젠 분해물",
  "Alkylbenzenes (C7-C8)": "알킬벤젠류 (C7-C8)",
  "n-Alkanes (C9-C10)": "n-알칸 (C9-C10)",
  "n-Alkane/C9 Aromatic Mix": "n-알칸/C9 방향족 혼합",
  "C9 Aromatic Cluster (Ethylbenzene/Xylene)": "C9 방향족 (에틸벤젠/자일렌)",
  "n-Alkane/C9 Aromatic Mix (C10-C12)": "n-알칸/C9 방향족 혼합 (C10-C12)",
  "Cotton Cellulose Pyrolysis (Furan/Ketone)":
    "면 셀룰로오스 열분해 (푸란/케톤)",
  "Naphthenes (PAHs precursor)": "나프텐류 (PAH 전구체)",
  "C9 Aromatic (TMB/Ethyltoluene)": "C9 방향족 (TMB/에틸톨루엔)",
  "n-Alkane/C9 Aromatic Mix (C12+)": "n-알칸/C9 방향족 혼합 (C12+)",
};

/**
 * 영문 화합물명을 한국어 통용명으로 변환.
 * 매핑 없으면 원문 그대로 반환 (hasMapping=false).
 *
 * @param predictedSubstance 영문 화합물명. undefined/empty → "—"
 */
export function getKoreanName(
  predictedSubstance: string | undefined,
): KoreanNameResult {
  if (!predictedSubstance || predictedSubstance.trim() === "") {
    return { ko: "—", en: "", hasMapping: false };
  }
  const en = predictedSubstance.trim();
  const ko = MAPPING[en];
  if (ko) {
    return { ko, en, hasMapping: true };
  }
  return { ko: en, en, hasMapping: false };
}
